/**
 * Supabase Database Diagnostics Utility
 *
 * Uses service role key for read-only access to validate database structure,
 * check data existence, and diagnose issues. This bypasses RLS for diagnostic purposes only.
 *
 * Environment variables required:
 * - VITE_SUPABASE_URL
 * - SUPABASE_SERVICE_ROLE_KEY (or VITE_SUPABASE_SERVICE_ROLE_KEY)
 */

import { createClient } from '@supabase/supabase-js';

/**
 * Get a Supabase client with service role key for read-only diagnostics
 * This bypasses RLS and should only be used for validation/diagnostics
 */
export const getSupabaseDiagnosticsClient = () => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const serviceRoleKey =
    import.meta.env.SUPABASE_SERVICE_ROLE_KEY || import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL environment variable is required');
  }

  if (!serviceRoleKey) {
    throw new Error(
      'SUPABASE_SERVICE_ROLE_KEY or VITE_SUPABASE_SERVICE_ROLE_KEY environment variable is required'
    );
  }

  return createClient(supabaseUrl, serviceRoleKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false
    }
  });
};

/**
 * Check if a table exists and is accessible
 */
export const checkTableExists = async (schema: string, tableName: string): Promise<boolean> => {
  try {
    const client = getSupabaseDiagnosticsClient();

    // For schema-qualified tables, use a client with that schema
    const schemaClient =
      schema === 'public'
        ? client
        : createClient(
            import.meta.env.VITE_SUPABASE_URL!,
            import.meta.env.SUPABASE_SERVICE_ROLE_KEY ||
              import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY!,
            { db: { schema }, auth: { autoRefreshToken: false, persistSession: false } }
          );

    const { error } = await schemaClient.from(tableName).select('*').limit(1);

    return !error;
  } catch (error) {
    return false;
  }
};

/**
 * Get row count from a table (for validation)
 */
export const getTableRowCount = async (schema: string, tableName: string): Promise<number> => {
  try {
    const client = getSupabaseDiagnosticsClient();

    const schemaClient =
      schema === 'public'
        ? client
        : createClient(
            import.meta.env.VITE_SUPABASE_URL!,
            import.meta.env.SUPABASE_SERVICE_ROLE_KEY ||
              import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY!,
            { db: { schema }, auth: { autoRefreshToken: false, persistSession: false } }
          );

    const { count, error } = await schemaClient
      .from(tableName)
      .select('*', { count: 'exact', head: true });

    if (error) {
      return -1;
    }

    return count || 0;
  } catch (error) {
    return -1;
  }
};

/**
 * Check if a user has entries in user_tenants
 */
export const checkUserTenants = async (
  userId: string
): Promise<{ exists: boolean; count: number; tenantIds: string[] }> => {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const serviceRoleKey =
      import.meta.env.SUPABASE_SERVICE_ROLE_KEY || import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

    // Use prod schema client for user_tenants
    const client = createClient(supabaseUrl!, serviceRoleKey!, {
      db: { schema: 'prod' },
      auth: {
        autoRefreshToken: false,
        persistSession: false
      }
    });

    const { data, error, count } = await client
      .from('user_tenants')
      .select('tenant_id', { count: 'exact' })
      .eq('user_id', userId);

    if (error) {
      return { exists: false, count: 0, tenantIds: [] };
    }

    return {
      exists: (data?.length || 0) > 0,
      count: count || 0,
      tenantIds: data?.map((row: any) => row.tenant_id).filter(Boolean) || []
    };
  } catch (error) {
    return { exists: false, count: 0, tenantIds: [] };
  }
};

/**
 * Validate tenant setup - check if prod.tenants exists and has data
 */
export const validateTenantSetup = async (): Promise<{
  prodTenantsExists: boolean;
  prodTenantsCount: number;
  userTenantsExists: boolean;
  userTenantsCount: number;
}> => {
  try {
    const prodTenantsExists = await checkTableExists('prod', 'tenants');
    const prodTenantsCount = prodTenantsExists ? await getTableRowCount('prod', 'tenants') : 0;
    const userTenantsExists = await checkTableExists('prod', 'user_tenants');
    const userTenantsCount = userTenantsExists ? await getTableRowCount('prod', 'user_tenants') : 0;

    return {
      prodTenantsExists,
      prodTenantsCount,
      userTenantsExists,
      userTenantsCount
    };
  } catch (error) {
    return {
      prodTenantsExists: false,
      prodTenantsCount: 0,
      userTenantsExists: false,
      userTenantsCount: 0
    };
  }
};
